/**
 * The match sun.
 *
 * Faithful port of `Match::SetRandomSunParams` (match.cpp:493-524) together with the light
 * declared in media/objects/lighting/generic.object. The original randomises the sun's
 * direction and colour once per match, so no two matches are lit identically.
 *
 * Values are in the SIMULATION frame (Z-up). Convert with core/coords before handing them
 * to the renderer.
 */

import { Vector3 } from 'three';
import { clamp, normalizedClamp } from '../core/math';

export interface SunParams {
  /** Direction * 10000, matching `sunNode->...->SetPosition(sunPos * 10000)` (match.cpp:504). */
  position: Vector3;
  color: Vector3;
  /**
   * 1,000,000 — from generic.object and match.cpp:507.
   *
   * This matters more than it looks. lighting.frag computes
   *   falloff = (max(0, radius - dist) / radius)^2
   * so a radius this large makes falloff ~1 everywhere on the pitch: the sun is effectively
   * infinitely distant with no attenuation. Using a plausible-looking small radius instead
   * makes brightness vary across the pitch and the whole image go wrong.
   */
  radius: number;
}

export const SUN_RADIUS = 1_000_000;

/**
 * `Match::SetRandomSunParams`, match.cpp:493-524.
 *
 * `random` has no default on purpose. This is simulation code, and nothing in the simulation
 * may draw from `Math.random` (see core/random.ts). A default here would be a landmine: harmless
 * today, but silent the moment some future call site — a new caller, a quick test, a debug
 * script — omits the argument and gets a non-reproducible sun with nothing to catch it. Every
 * caller states its generator explicitly instead; see defaultSun() below for the fixed one.
 */
export function makeSun(random: () => number): SunParams {
  const rand = (min: number, max: number) => min + random() * (max - min);

  // match.cpp:500-501. Note the range is wider than the clamp, so the result is biased
  // towards the extremes rather than uniform — that is the original's behaviour.
  const averageHeightMultiplier = 1.3;
  const position = new Vector3(
    clamp(rand(-1.7, 1.7), -1, 1),
    clamp(rand(-1.7, 1.7), -1, 1),
    averageHeightMultiplier,
  ).normalize();

  // match.cpp:502 — the sun sits on the default camera's side more often than not, because
  // light coming from the front reads more clearly on the players.
  if (random() > 0.5 && position.y > 0.25) position.y = -position.y;

  // match.cpp:511-518
  const noon = new Vector3(0.9, 0.8, 1.0).multiplyScalar(1.4);
  const dusk = new Vector3(1.4, 0.9, 0.7).multiplyScalar(1.2);
  const noonBias = Math.pow(normalizedClamp(position.z, 0.5, 1.0), 1.2);

  const color = noon
    .clone()
    .multiplyScalar(noonBias)
    .add(dusk.clone().multiplyScalar(1 - noonBias));

  // match.cpp:520-522
  color.add(
    new Vector3(rand(-0.1, 0.1), rand(-0.1, 0.1), rand(-0.1, 0.1)).multiplyScalar(1.2),
  );

  return {
    position: position.multiplyScalar(10000),
    color, // brightness is 1.0 in the original (match.cpp:497), so no further scaling
    radius: SUN_RADIUS,
  };
}

/**
 * A deterministic sun — a real draw from the same distribution, with a fixed seed.
 *
 * NOT the "sane default" written at match.cpp:499. That line is dead code: the very next
 * statement overwrites it (match.cpp:501), and reproducing it gives a sun lower and warmer
 * than any actual match produces. The real height multiplier is 1.3 before normalising, so
 * a match sun's height component always lands in roughly 0.63..1.0, where that dead value
 * sits at 0.62 — the extreme dusk end of the range.
 *
 * Use this for screenshots, comparisons against the original, and tests. Use makeSun() in
 * play, where per-match variation is the point.
 */
export function defaultSun(): SunParams {
  return makeSun(mulberry32(0x9e3779b9));
}

/** Small deterministic generator, so a fixed seed gives a repeatable sun. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
